import { css } from 'astroturf';
import React from 'react';
import Link from '../link';

import Brow from '../Brow';

const styles = css`
  .faq {
    max-width: 45.5em;
    margin: 0 auto 5rem;
    color: rgb(10, 21, 48);

    & h2 {
      font-weight: 900;
      font-size: clamp(18px, 16px + 4vw, 44px);
      letter-spacing: -0.015em;
      line-height: 1.25;
      margin: auto;
      margin-bottom: 0.5rem;
      margin-top: 0;
      max-width: 60rem;
    }

    summary {
      padding: 1.5rem 1rem 1rem;
      color: rgb(0, 124, 165);
      font-size: 1.125rem;
      transition: 0.25s ease-in-out color;
      display: block;
    }

    .content {
      padding: 0rem 1rem 2rem;
    }

    details::details-content {
      overflow: clip;
      text-wrap: pretty;
      interpolate-size: allow-keywords;
      opacity: 0;
      height: 0;
      transition:
        opacity 0.25s ease-in-out,
        height 0.25s ease-in-out,
        content-visibility 0.25s ease-in-out allow-discrete;
    }

    details[open]::details-content {
      opacity: 1;
      height: auto;
    }
  }

  .group {
    & h3 {
      font-size: 1rem;
      font-weight: 700;
      color: rgb(10, 21, 48);
      opacity: 0.6;
      margin: 0;
      padding: 0 1rem 0.5rem;
      text-align: left;
    }
  }

  .group {
    margin-top: 1.5rem;
  }

  .group + .group {
    margin-top: 3rem;
  }

  .item {
    text-align: left;
  }
  .item + .item {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

class FAQ extends React.Component {
  render() {
    return (
      <div className={styles.faq}>
        <Brow>Pricing FAQ</Brow>
        <h2>Frequently Asked Questions</h2>
        {faqs.map(({ group, items }) => (
          <>
            {items.map((faq) => (
              <details className={styles.item} key={faq.q} open>
                <summary className={styles.header}>{faq.q}</summary>
                <div className={styles.content}>
                  <div className="childPadding">{faq.a}</div>
                </div>
              </details>
            ))}
          </>
        ))}
      </div>
    );
  }
}

export default FAQ;

const faqs = [
  {
    group: 'Choosing a plan',
    items: [
      {
        q: 'Do I need a trial account before subscribing?',
        a: (
          <>
            If you’re already familiar with Polypane and want to subscribe with a new email address, you can do so
            directly and we will create an account for you during checkout. If you want to try Polypane first, you can
            start a free trial and then subscribe from your trial account. If you already have a trial account, the
            subscription automatically gets applied to it.
          </>
        ),
      },
      {
        q: 'Can I share my Individual license with others?',
        a: (
          <>
            You can activate your Polypane Individual license on up to 3 computers but it is exclusive to one user only.
            If you need a license for multiple users, a Business or Enterprise license could be for you.
          </>
        ),
      },
      {
        q: 'Why do all licenses include 3 devices?',
        a: (
          <>
            <p>
              As a web developer (or someone who checks websites) you probably use more than one device. Since rendering
              is different on different operating systems, even if you use the same browser, you should test on multiple
              operating systems too.
            </p>
            <p>
              We think your development tools should be available where you work, without them forcing you to
              register/unregister them constantly. Polypane includes three devices so you can run it across Mac, Windows
              and Linux devices without any hassle.
            </p>
          </>
        ),
      },
      {
        q: 'What is a Business license and can I scale my team?',
        a: (
          <>
            <p>
              A Polypane Business license lets you use Polypane with your entire team and includes team management
              options. The Team manager can grant and revoke licenses from the Polypane{' '}
              <a href="https://dashboard.polypane.app/">Dashboard</a> directly.
            </p>
            <p>
              You can pick anywhere from 2 to 20 seats when you subscribe, and add or remove seats yourself at any
              time from the Dashboard. Need more than 20? Please <a href="/support/">contact us</a>.
            </p>
          </>
        ),
      },
      {
        q: 'I have an Individual license, can I upgrade to a Business license?',
        a: (
          <>
            Yes, you can upgrade your account from Individual to Business on the{' '}
            <a href="https://dashboard.polypane.app/">Dashboard</a>. You will be upgraded to a Business license with the
            equivalent billing period (monthly or yearly). Please reach out for any other questions.
          </>
        ),
      },
      {
        q: 'Do you offer purchasing power parity (PPP)?',
        a: (
          <>
            Yes, we offer PPP for countries with significantly lower average income to make our prices fair for
            everyone. <a href="/support/">Contact us</a> with proof of residence to receive a discount code.
          </>
        ),
      },
      {
        q: 'Do you have educational or non-profit discounts?',
        a: (
          <>
            Polypane is available for free through the{' '}
            <a href="https://education.github.com/pack">GitHub Student Developer Pack</a> and there are various{' '}
            <Link to="/special-offers/">special offers</Link> available for educators, educational institutions and
            charities. Please <a href="/support/">contact us</a> for more information.
          </>
        ),
      },
      {
        q: 'How can I convince my team or manager to adopt Polypane?',
        a: (
          <>
            The <Link to="/docs/pitching-polypane/">Pitching Polypane</Link> page covers the most common objections and
            how to address them, from “our current setup works fine” to IT and security approval. You can also use our{' '}
            <Link to="/dear-manager/">example letter</Link> to ask your manager for a subscription, and the{' '}
            <Link to="/calculator/">ROI calculator</Link> to show exactly how quickly the licence pays for itself at
            your team’s hourly rate.
          </>
        ),
      },
    ],
  },
  {
    group: 'Paying',
    items: [
      {
        q: 'What payment methods are available?',
        a: 'You can pay securely with credit card and PayPal, as well as Apple Pay and Google Pay where available. Custom invoicing and purchase orders are available with the Enterprise plan.',
      },
      {
        q: 'Who processes Polypane orders?',
        a: (
          <>
            Our order process is conducted securely by our online reseller Paddle. Paddle is the Merchant of Record for
            all our orders and they will invoice and charge you.
          </>
        ),
      },
      {
        q: 'What does the charge on my bank statement say?',
        a: (
          <>
            Because Paddle takes the payment, the charge shows up under their name rather than under Polypane, as
            “Paddle.net” followed by the reference <strong>POLYPANE</strong>. Paddle processes payments for a great many
            companies, so a charge from them is only ours if it carries that reference. Your invoice lists both Paddle
            and Polypane, and you can find every invoice in the subscription overview on the{' '}
            <a href="https://dashboard.polypane.app/">Dashboard</a>. If a charge still looks unfamiliar,{' '}
            <a href="/support/">contact us</a> and we will look it up for you.
          </>
        ),
      },
      {
        q: 'Why do I need to pay Tax/VAT?',
        a: (
          <>
            VAT is a required sales tax in some countries. Fill in your company details during checkout (or after
            getting the invoice) and the VAT will either be deducted or you can use the invoice to reclaim the VAT from
            your tax authority.
          </>
        ),
      },
      {
        q: 'Do I get an invoice?',
        a: (
          <>
            Yes. An invoice is automatically generated and e-mailed to you (or the billing email address you specified)
            after going through the checkout. You can also download all previous invoices from the subscription overview
            on the <a href="https://dashboard.polypane.app/">Dashboard</a>.
          </>
        ),
      },
      {
        q: 'Can I add my company name to the invoice?',
        a: (
          <>
            Yes. During checkout you can fill in your company details and an invoice is automatically generated and
            e-mailed to you. It is also possible to add your company details after checkout by opening the invoice you
            got and adding the details there.
          </>
        ),
      },
      {
        q: 'Can you provide a quote for my purchase order?',
        a: (
          <>
            <p>
              For the Individual and Business plans you can <a href="/pricing/quote/">generate a quote</a>. Pick your
              plan and billing period, add your organization details and your PO number, and save the result as a PDF.
            </p>
            <p>
              It lists the subscription, the price, any sales tax and the total, which is what most purchasing systems
              ask for. Because our checkout is fully automated by our payment partner Paddle, that quote is indicative
              rather than a binding offer, and the prices on this page are always leading.
            </p>
            <p>
              We are happy to put together a custom quote for Enterprise purchases. Please{' '}
              <a href="/support/">contact us</a> with your requirements and we will get back to you with a quote.
            </p>
          </>
        ),
      },
      {
        q: 'Can you go through our procurement/vendor approval process?',
        a: (
          <>
            For the Individual and Business plans, we ask you use the info on our{' '}
            <Link to="/docs/sales-and-procurement/">Sales and procurement</Link> page to go through your procurement
            process. For Enterprise purchases, we do offer assistance. Please <a href="/support/">contact us</a> for a
            custom quote and to go through your procurement process.
          </>
        ),
      },
      {
        q: 'Why is my credit card or PayPal payment declined?',
        a: (
          <>
            The decline almost always comes from your bank or from PayPal, for reasons like insufficient funds, a
            transaction they flag as fraudulent, a mistyped CVC/CVV or, with PayPal, no bank account or credit card
            linked to the account. Banks and PayPal rarely pass on the real reason, so if none of those apply, your bank
            is the only one who can tell you what went wrong.
          </>
        ),
      },
    ],
  },
  {
    group: 'After you subscribe',
    items: [
      {
        q: 'How do I make changes to my subscription plan?',
        a: (
          <>
            Log in to the <a href="https://dashboard.polypane.app/">Dashboard</a> and go to the Subscription tab to make
            changes.
          </>
        ),
      },
      {
        q: 'What is your refund policy?',
        a: (
          <>
            <p>
              You are eligible for a refund within 14 days of your original purchase date. If Polypane is not for you in
              that period, <a href="/support/">contact us</a> and we will refund you.
            </p>
            <p>
              After that, it depends on your billing period. On a yearly plan, if you forgot to cancel before the
              renewal date, reach out within 7 days of that renewal and we will discuss a refund, minus the transaction
              fee Paddle charges us. On a monthly plan renewals are not refunded, so cancel before the monthly renewal
              date.
            </p>
          </>
        ),
      },
      {
        q: 'Where can I find the terms of service and privacy policy?',
        a: (
          <>
            Read our <a href="/legal/">terms of service</a> and our <a href="/privacy/">privacy policy</a>.
          </>
        ),
      },
    ],
  },
];
